import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from './api';

const UserContext = createContext();

export function UserProvider({ children }) {
  const [selectedUser, setSelectedUserState] = useState(null);
  const [recentUsers, setRecentUsers] = useState([]);

  // Load from localStorage on mount
  useEffect(() => {
    const env = localStorage.getItem('selectedEnvironment') || 'production';
    const stored = localStorage.getItem(`selectedUser_${env}`);
    if (stored) {
      try {
        setSelectedUserState(JSON.parse(stored));
      } catch (error) {
        // Invalid JSON in localStorage, ignore and use default
        console.warn('Failed to parse stored user data:', error);
      }
    }

    const storedRecent = localStorage.getItem(`recentUsers_${env}`);
    if (storedRecent) {
      try {
        setRecentUsers(JSON.parse(storedRecent));
      } catch (error) {
        // Invalid JSON in localStorage, ignore and use default
        console.warn('Failed to parse stored recent users:', error);
      }
    }
  }, []);

  // Save to localStorage on change
  const setSelectedUser = useCallback((user) => {
    setSelectedUserState(user);
    const env = localStorage.getItem('selectedEnvironment') || 'production';
    if (user) {
      localStorage.setItem(`selectedUser_${env}`, JSON.stringify(user));
    } else {
      localStorage.removeItem(`selectedUser_${env}`);
    }
  }, []);

  // Add user to recent users list
  const addToRecentUsers = useCallback((user) => {
    if (!user || !user.id) return;

    setRecentUsers(prev => {
      // Remove user if already exists
      const filtered = prev.filter(u => u.id !== user.id);
      // Add to beginning of list
      const updated = [user, ...filtered].slice(0, 10); // Keep only last 10 users
      
      // Save to localStorage with environment prefix
      const env = localStorage.getItem('selectedEnvironment') || 'production';
      localStorage.setItem(`recentUsers_${env}`, JSON.stringify(updated));
      
      return updated;
    });
  }, []);

  // Unified method to select user by ID with full data fetch
  const selectUserById = useCallback(async (userId, navigate) => {
    if (!userId) return;
    
    try {
      const response = await api.get(`/users/${userId}`);
      const userData = response.data;
      setSelectedUser(userData);
      addToRecentUsers(userData);
      if (navigate) {
        navigate(`/user/${userId}/details`);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      // Fallback: create minimal user object if fetch fails
      setSelectedUser({ id: userId });
      if (navigate) {
        navigate(`/user/${userId}/details`);
      }
    }
  }, [setSelectedUser, addToRecentUsers]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    selectedUser,
    setSelectedUser,
    selectUserById,
    recentUsers,
    addToRecentUsers
  }), [selectedUser, setSelectedUser, selectUserById, recentUsers, addToRecentUsers]);

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}
